import { Flex, RadioContext, Spacer } from '@lokalise/louis';

import { FileFormat } from '../../types';
import ExportAccordion from '../ExportAccordion';
import FilenamesCustomInput from './components/FilenamesCustomInput';
import FilenamesCustomRadio from './components/FilenamesCustomRadio';
import FilenamesOriginalInput from './components/FilenamesOriginalInput';
import FilenamesOriginalRadio from './components/FilenamesOriginalRadio';
import UnassignedKeysWarning from './components/UnassignedKeysWarning';

type FileNamesValue = 'filenamesCustom' | 'filenamesOriginal';

type Value = Record<FileNamesValue, boolean> & {
    zipStructure: string;
    directoryPrefix: string;
};

const getFilenamesIncomingValue = (value: Record<FileNamesValue, boolean>) =>
    value.filenamesCustom ? 'filenamesCustom' : 'filenamesOriginal';

const getFilenamesOutgoingValue = (value: FileNamesValue): Record<FileNamesValue, boolean> => ({
    filenamesCustom: value === 'filenamesCustom',
    filenamesOriginal: value !== 'filenamesCustom',
});

interface CustomFileAndFolderStructureProps {
    value: Value;
    fileFormat: FileFormat;
    includeAllPlatformKeys: boolean;
    projectId: string;
    onChange: (value: Value) => void;
}

const CustomFileAndFolderStructure = ({
    value,
    fileFormat,
    includeAllPlatformKeys,
    projectId,
    onChange,
}: CustomFileAndFolderStructureProps) => {
    const isLimitedFileStructure = [FileFormat.iosSdk, FileFormat.multilingualExcel].includes(fileFormat);
    const filenamesValue = getFilenamesIncomingValue(value);

    return (
        <section aria-label="File structure">
            <ExportAccordion
                heading={<b>File structure</b>}
                name="custom-file-and-folder-structure"
                projectId={projectId}
            >
                <Spacer top={4}>
                    <RadioContext
                        name="filenames"
                        value={filenamesValue}
                        onChange={(_, val) =>
                            onChange({
                                ...value,
                                ...getFilenamesOutgoingValue(val as FileNamesValue),
                            })
                        }
                    >
                        <Flex gap={4} direction="column" align="stretch">
                            <Flex gap={2} direction="column" align="stretch">
                                <FilenamesCustomRadio isLimitedFileStructure={isLimitedFileStructure} />
                                {!isLimitedFileStructure && (
                                    <FilenamesCustomInput
                                        value={value.zipStructure}
                                        fileFormat={fileFormat}
                                        onChange={(zipStructure) =>
                                            onChange({
                                                ...value,
                                                zipStructure,
                                            })
                                        }
                                    />
                                )}
                            </Flex>
                            <Flex gap={2} direction="column" align="stretch">
                                <FilenamesOriginalRadio isLimitedFileStructure={isLimitedFileStructure} />
                                {!isLimitedFileStructure && (
                                    <Flex gap={1} direction="column" align="stretch">
                                        <FilenamesOriginalInput
                                            value={value.directoryPrefix}
                                            fileFormat={fileFormat}
                                            onChange={(directoryPrefix) =>
                                                onChange({
                                                    ...value,
                                                    directoryPrefix,
                                                })
                                            }
                                        />
                                        {filenamesValue === 'filenamesOriginal' && (
                                            <UnassignedKeysWarning
                                                fileFormat={fileFormat}
                                                includeAllPlatformKeys={includeAllPlatformKeys}
                                            />
                                        )}
                                    </Flex>
                                )}
                            </Flex>
                        </Flex>
                    </RadioContext>
                </Spacer>
            </ExportAccordion>
        </section>
    );
};

export default CustomFileAndFolderStructure;
